// v2 refinery — a copper pot-still works: a round stone furnace with a glowing
// fire mouth carries a fat copper kettle under an onion dome, its swan-neck pipe
// arching over into a coil-wrapped timber condenser vat whose spout runs the
// cooled fuel into an open keg glowing amber; a brick smoke stack puffs behind
// the works and a timber rack of finished fuel barrels lies along the west side.
// Ported 1:1 from age-of-mcp's v2/refinery.ts, using the library's shared
// `foundationSlab`, `scaffold` and `industry` (emberMouth) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, industry, benchWork, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const STONE   = "#7a6a58";  // furnace stone
const COPPER  = "#b06a36";  // kettle / swan-neck
const COPPERD = "#8a4f28";  // kettle band / neck / spout
const COIL    = "#c9803e";  // condenser coil rings
const VAT     = "#7a5a34";  // condenser vat staves
const KEG     = "#8a5d2e";  // fuel kegs / barrels
const AMBER   = "#e8a830";  // the fuel itself
const BRICK   = "#6e4a3e";  // smoke stack
const DARK    = "#4a4a4e";  // iron lids / hoops / valve
const SMOKE   = "#c4c4c4";
const RAIL    = "#5e4528";  // barrel-rack rails

const BASE = 0.14;                  // foundation slab top
const FX = -0.14, FZ = -0.02;       // furnace + kettle axis
const VX = 0.26, VZ = -0.02;        // condenser vat axis

// The round stone furnace, `h` tall on the foundation.
function furnace(h: number): Part {
  return { geometry: new THREE.CylinderGeometry(0.19, 0.23, h, 8), color: STONE,
    position: [FX, BASE + h / 2, FZ] };
}

// The copper kettle drum on the furnace shoulder (partial builds pass a
// lower `h`), with its riveted band.
function kettle(h: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.17, 0.17, h, 8), color: COPPER,
      position: [FX, 0.44 + h / 2, FZ] },
    { geometry: new THREE.CylinderGeometry(0.172, 0.172, 0.025, 8), color: COPPERD,
      position: [FX, 0.48, FZ] },
  ];
}

// The onion dome, its neck, and the two-segment swan-neck pipe arching over
// to the condenser vat's lid.
function swanNeck(): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.165, 8, 6), color: COPPER,
      position: [FX, 0.64, FZ], scale: [1, 0.72, 1] },
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.10, 6), color: COPPERD,
      position: [FX, 0.78, FZ] },
    { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.21, 5), color: COPPER,
      position: [-0.04, 0.805, FZ], rotation: [0, 0, -1.325] },
    { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.27, 5), color: COPPER,
      position: [0.16, 0.695, FZ], rotation: [0, 0, -0.868] },
  ];
}

// The coil-wrapped condenser vat with its iron lid, hand valve, spout and
// the keg of fresh amber fuel filling under it.
function condenser(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.135, 0.135, 0.44, 8), color: VAT,
      position: [VX, BASE + 0.22, VZ] },
    { geometry: new THREE.CylinderGeometry(0.14, 0.14, 0.03, 8), color: DARK,
      position: [VX, 0.595, VZ] },
    { geometry: new THREE.CylinderGeometry(0.15, 0.15, 0.035, 8), color: COIL,
      position: [VX, 0.26, VZ] },
    { geometry: new THREE.CylinderGeometry(0.15, 0.15, 0.035, 8), color: COIL,
      position: [VX, 0.36, VZ] },
    { geometry: new THREE.CylinderGeometry(0.15, 0.15, 0.035, 8), color: COIL,
      position: [VX, 0.46, VZ] },
    { geometry: new THREE.CylinderGeometry(0.03, 0.03, 0.015, 6), color: DARK,
      position: [VX, 0.30, VZ + 0.145], rotation: [Math.PI / 2, 0, 0] },
    { geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.14, 5), color: COPPERD,
      position: [VX, 0.235, VZ + 0.195], rotation: [2.0, 0, 0] },
    { geometry: new THREE.CylinderGeometry(0.07, 0.065, 0.15, 8), color: KEG,
      position: [VX, BASE + 0.075, VZ + 0.315] },
    { geometry: new THREE.CylinderGeometry(0.052, 0.052, 0.014, 8), color: AMBER,
      position: [VX, BASE + 0.156, VZ + 0.315] },
  ];
}

// The brick smoke stack behind the works; its puffs are the animated plume.
function stack(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.10, 0.56, 0.10), color: BRICK,
      position: [-0.31, BASE + 0.28, -0.26] },
    { geometry: new THREE.BoxGeometry(0.13, 0.04, 0.13), color: DARK,
      position: [-0.31, 0.72, -0.26] },
    ...plume.parts,
  ];
}

// The "still is running" cue: smoke off the stack while the furnace's ember
// mouth pulses.
const plume = smokePlume([-0.31, 0.75, -0.26], { rise: 0.32, radius: 0.05, color: SMOKE });

// The rack of finished fuel barrels on the west side: two rails carrying two
// hooped barrels with a third nested on top.
function barrelRack(): Part[] {
  const lie: [number, number, number] = [Math.PI / 2, 0, 0];
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.26, 0.04, 0.05), color: RAIL,
      position: [-0.29, BASE + 0.04, 0.16] },
    { geometry: new THREE.BoxGeometry(0.26, 0.04, 0.05), color: RAIL,
      position: [-0.29, BASE + 0.04, 0.32] },
  ];
  for (const [bx, by] of
       [[-0.345, BASE + 0.135], [-0.235, BASE + 0.135], [-0.29, BASE + 0.265]] as const) {
    parts.push({ geometry: new THREE.CylinderGeometry(0.075, 0.075, 0.24, 8),
      color: KEG, position: [bx, by, 0.24], rotation: lie });
  }
  parts.push({ geometry: new THREE.CylinderGeometry(0.078, 0.078, 0.02, 8), color: DARK,
    position: [-0.345, BASE + 0.135, 0.24], rotation: lie });
  parts.push({ geometry: new THREE.CylinderGeometry(0.078, 0.078, 0.02, 8), color: DARK,
    position: [-0.235, BASE + 0.135, 0.24], rotation: lie });
  return parts;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) {
    return [foundationSlab(0.84), furnace(0.30),
      ...industry.emberMouth(FX, 0.27, 0.195, 0.20, 0.15), ...scaffold(0.82, 0.55)];
  }
  if (s === 2) {
    return [foundationSlab(0.84), furnace(0.30),
      ...industry.emberMouth(FX, 0.27, 0.195, 0.20, 0.15),
      ...kettle(0.12),
      { geometry: new THREE.CylinderGeometry(0.135, 0.135, 0.30, 8), color: VAT,
        position: [VX, BASE + 0.15, VZ] },
      ...scaffold(0.82, 0.9)];
  }
  return [
    foundationSlab(0.84),
    furnace(0.30),
    ...industry.emberMouth(FX, 0.27, 0.195, 0.20, 0.15, "embers"),
    ...kettle(0.20),
    ...swanNeck(),
    ...condenser(),
    ...stack(),
    ...barrelRack(),
  ];
}

export const refineryV2 = defineModel({
  id: "refinery-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Refinery (v2)",
  tags: ["building","refinery","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly refinery.",
  description: "A still that cooks raw wood into fuel.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Still running",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [...plume.tracks, firePulse("embers", [FX, 0.27, 0.21])],
    },
  ],
  stations: [
    { id: "bench", position: [-0.14, 0.14, 0.44], facing: Math.PI },
  ],
  interactions: [
    { id: "refine", label: "At the still", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
